import { create } from 'zustand';
import { CANVAS_VIEWS, type CoreCanvasView } from '../types';

// --- Workspace shell (SC-2873) — the tab bar as a SELECTION from the library ---
//
// Which views sit on a catalog's tab bar: the entities view is the locked
// anchor (never listed here, never removable), core views can be taken off the
// bar (back to the library shelf — never deleted), and boards are pinned in
// pin order. This is the view-level sibling of `boardShellStore` (tile-level):
// same two-producers rule — the LIBRARY is canonical, the bar is a per-viewer
// selection over it, so removing is always "a workspace preference, not a
// deletion".
//
// Session-scoped module memory for now; the durable home is the server-side
// workspace shell (SC-2870: `{core_views_in_use, boards_in_use, …}` per
// user + catalog, merged system base ← org default ← personal). When that
// lands, this store hydrates from / writes through to it. Registered with
// `clearSessionState` (SC-2461).

/** Core views that can be selected on/off the bar — everything but the anchor. */
export type SelectableCoreView = Exclude<CoreCanvasView, 'entities'>;

/** Canonical bar order for core views (entities always leads, so it's not here). */
export const SELECTABLE_CORE_VIEWS: readonly SelectableCoreView[] = CANVAS_VIEWS.filter(
  (v): v is SelectableCoreView => v !== 'entities',
);

export interface WorkspaceShellSlice {
  /** Core views currently on the bar, in canonical order. */
  coreViewsInUse: SelectableCoreView[];
  /** Board ids pinned to the bar, in pin order. */
  boardsInUse: string[];
}

/** The EFFECTIVE pinned-board ids: `required` org boards are on every member's
 *  bar whether or not they ever pinned them (the SC-2870 `required` contract —
 *  the org-default layer merged under the personal selection), ahead of the
 *  personal pins. ONE predicate for every surface that asks "is this board on
 *  the bar?" — the tab bar, the library's row state, and the board route's
 *  ARIA all derive from this, so they can never disagree. */
export function effectiveBoardsInUse(
  slice: WorkspaceShellSlice,
  boards: readonly { id: string; required: boolean }[],
): string[] {
  const required = boards.filter((b) => b.required).map((b) => b.id);
  return [...required, ...slice.boardsInUse.filter((id) => !required.includes(id))];
}

/** The system default workspace: every core view on the bar, no boards. */
export const DEFAULT_WORKSPACE_SHELL: WorkspaceShellSlice = {
  coreViewsInUse: [...SELECTABLE_CORE_VIEWS],
  boardsInUse: [],
};

interface WorkspaceShellStore {
  byCatalog: Record<string, WorkspaceShellSlice>;
  addCoreView: (catalogId: string, view: SelectableCoreView) => void;
  removeCoreView: (catalogId: string, view: SelectableCoreView) => void;
  addBoard: (catalogId: string, boardId: string) => void;
  removeBoard: (catalogId: string, boardId: string) => void;
  /** Wipe every selection on an identity change (`clearSessionState`). */
  reset: () => void;
}

function withSlice(
  state: WorkspaceShellStore,
  catalogId: string,
  patch: (slice: WorkspaceShellSlice) => Partial<WorkspaceShellSlice>,
): Pick<WorkspaceShellStore, 'byCatalog'> {
  const slice = state.byCatalog[catalogId] ?? DEFAULT_WORKSPACE_SHELL;
  return { byCatalog: { ...state.byCatalog, [catalogId]: { ...slice, ...patch(slice) } } };
}

export const useWorkspaceShellStore = create<WorkspaceShellStore>((set) => ({
  byCatalog: {},

  addCoreView: (catalogId, view) =>
    set((state) =>
      withSlice(state, catalogId, (slice) => ({
        // Re-derive from the canonical order so adding a view back never
        // depends on removal order.
        coreViewsInUse: SELECTABLE_CORE_VIEWS.filter(
          (v) => slice.coreViewsInUse.includes(v) || v === view,
        ),
      })),
    ),

  removeCoreView: (catalogId, view) =>
    set((state) =>
      withSlice(state, catalogId, (slice) => ({
        coreViewsInUse: slice.coreViewsInUse.filter((v) => v !== view),
      })),
    ),

  addBoard: (catalogId, boardId) =>
    set((state) =>
      withSlice(state, catalogId, (slice) => ({
        boardsInUse: slice.boardsInUse.includes(boardId)
          ? slice.boardsInUse
          : [...slice.boardsInUse, boardId],
      })),
    ),

  removeBoard: (catalogId, boardId) =>
    set((state) =>
      withSlice(state, catalogId, (slice) => ({
        boardsInUse: slice.boardsInUse.filter((id) => id !== boardId),
      })),
    ),

  reset: () => set({ byCatalog: {} }),
}));
